<template>
  <div class="product-detail" v-if="product">
    <button class="back-button" @click="goBack">
      ← {{ t('product.detail.back') }}
    </button>

    <div class="detail-container">
      <div class="detail-header">
        <h1 class="product-title">{{ product.Title }}</h1>
        <div class="product-badges">
          <span class="badge badge-category">{{ getCategoryName(currentCategory) }}</span>
          <span class="badge badge-package">{{ product.Package }}</span>
          <span class="badge badge-status" :class="getStatusClass(product.ProductStatus)">
            {{ product.ProductStatus }}
          </span>
        </div>
      </div>

      <div class="detail-content">
        <div class="detail-section">
          <h2>{{ t('product.detail.basic_info') }}</h2>
          <div class="info-grid">
            <div class="info-item" v-for="(value, key) in getBasicInfo()" :key="key">
              <span class="info-label">{{ getDisplayName(key) }}:</span>
              <span class="info-value">{{ value }}</span>
            </div>
          </div>
        </div>

        <div class="detail-section" v-if="hasTechnicalParams()">
          <h2>{{ t('product.detail.technical_params') }}</h2>
          <div class="info-grid">
            <div class="info-item" v-for="(value, key) in getTechnicalParams()" :key="key">
              <span class="info-label">{{ getDisplayName(key) }}:</span>
              <span class="info-value">{{ value }}</span>
            </div>
          </div>
        </div>

        <div class="detail-section" v-if="product.FileUrl">
          <h2>{{ t('product.detail.documents') }}</h2>
          <a :href="getFileUrl(product.FileUrl)" target="_blank" class="download-link">
            📄 {{ t('product.detail.download_pdf') }}
          </a>
        </div>

        <div class="detail-section actions">
          <button class="action-button primary" @click="openPurchaseForm">
            {{ t('product.purchase_button') }}
          </button>
        </div>
      </div>
    </div>

    <!-- 购买表单 -->
    <PurchaseForm 
      v-if="showPurchaseForm" 
      :product="product"
      @close="closePurchaseForm"
    />
  </div>
  <div v-else class="loading">
    {{ t('product.detail.loading') }}
  </div>
</template>

<script>
import { useI18n } from 'vue-i18n';
import { useHead } from '@unhead/vue';
import { computed } from 'vue';
import PurchaseForm from './PurchaseForm.vue';

export default {
  name: 'ProductDetail',
  components: {
    PurchaseForm
  },
  props: {
    product: {
      type: Object,
      default: null // 从列表进入时有值；直接访问 /product/:id 时为 null，加载中显示 loading
    },
    currentCategory: {
      type: String,
      default: 'xfjMosfet'
    }
  },
  setup(props) {
    const { t, locale } = useI18n();
    
    // SEO 优化
    const categoryNames = {
      'xfjMosfet': { 'zh-CN': '消费级MOSFET', 'en-US': 'Consumer MOSFET', 'vi-VN': 'MOSFET Tiêu dùng', 'zh-TW': '消費級MOSFET' },
      'gyjMosfet': { 'zh-CN': '工业级MOSFET', 'en-US': 'Industrial MOSFET', 'vi-VN': 'MOSFET Công nghiệp', 'zh-TW': '工業級MOSFET' },
      'cgjMosfet': { 'zh-CN': '车规级MOSFET', 'en-US': 'Automotive MOSFET', 'vi-VN': 'MOSFET Ô tô', 'zh-TW': '車規級MOSFET' },
      'powermodule': { 'zh-CN': '功率模块', 'en-US': 'Power Module', 'vi-VN': 'Mô-đun Nguồn', 'zh-TW': '功率模組' },
      'dcdc': { 'zh-CN': 'DC-DC转换器', 'en-US': 'DC-DC Converter', 'vi-VN': 'Bộ chuyển đổi DC-DC', 'zh-TW': 'DC-DC轉換器' }
    };
    
    const categoryName = computed(() => {
      return categoryNames[props.currentCategory]?.[locale.value] || props.currentCategory;
    });
    
    const seoTitle = computed(() => {
      if (!props.product?.Title) return '765.info';
      return `${props.product.Title} - ${categoryName.value} | 765.info`;
    });
    
    const seoKeywords = computed(() => {
      if (!props.product) return '765.info, MOSFET, Power Module';
      const parts = [
        props.product.Title,
        props.product.Package,
        props.product.MOSFETType,
        categoryName.value,
        '765.info'
      ].filter(Boolean);
      return parts.join(', ');
    });
    
    const seoDescription = computed(() => {
      if (!props.product) return 'Professional power semiconductor solutions provider.';
      const parts = [];
      if (props.product.Title) parts.push(props.product.Title);
      if (props.product.Package) parts.push(`Package: ${props.product.Package}`);
      if (props.product.ProductStatus) parts.push(`Status: ${props.product.ProductStatus}`);
      if (props.product.IDA) parts.push(`Current: ${props.product.IDA}A`);
      if (props.product.VDSMin) parts.push(`Voltage: ${props.product.VDSMin}V`);
      return `${categoryName.value} - ${props.product.Title}. ${parts.slice(1).join(', ')}. Professional power semiconductor solutions from 765.info.`;
    });
    
    useHead(computed(() => ({
      title: seoTitle.value,
      meta: [
        { name: 'keywords', content: seoKeywords.value },
        { name: 'description', content: seoDescription.value },
        { property: 'og:title', content: seoTitle.value },
        { property: 'og:description', content: seoDescription.value },
        { property: 'og:type', content: 'product' }
      ]
    })));
    
    return { t };
  },
  data() {
    return {
      showPurchaseForm: false
    };
  },
  methods: {
    goBack() {
      this.$emit('close');
    },
    getCategoryName(category) {
      const map = {
        xfjMosfet: 'consumer_mosfet',
        gyjMosfet: 'industrial_mosfet',
        cgjMosfet: 'automotive_mosfet',
        powermodule: 'power_module',
        dcdc: 'dcdc'
      };
      const key = map[category];
      return key ? this.t('product.categories.' + key) : category;
    },
    getDisplayName(key) {
      return this.t('product.detail.fields.' + key) || key;
    },
    getBasicInfo() {
      const basicFields = ['Title', 'Package', 'ProductStatus', 'MOSFETType', 'Technology'];
      const info = {};
      basicFields.forEach(field => {
        if (this.product[field]) {
          info[field] = this.product[field];
        }
      });
      return info;
    },
    getTechnicalParams() {
      const techFields = ['IDA', 'VDSMin', 'RDSon', 'VGS', 'Qg', 'Ciss', 'Coss', 'Crss'];
      const params = {};
      techFields.forEach(field => {
        if (this.product[field]) {
          params[field] = this.product[field];
        }
      });
      return params;
    },
    hasTechnicalParams() {
      return Object.keys(this.getTechnicalParams()).length > 0;
    },
    getStatusClass(status) {
      const statusMap = {
        '量产': 'status-active',
        '开发中': 'status-developing',
        '停产': 'status-discontinued',
        'Preview': 'status-preview'
      };
      return statusMap[status] || 'status-default';
    },
    getFileUrl(url) {
      if (!url) return '';
      if (url.startsWith('http://') || url.startsWith('https://')) {
        return url;
      }
      // 数据手册托管在 api.765.info/pdf/ 下，拼接成完整链接
      const base = process.env.VUE_APP_PDF_BASE || 'https://api.765.info/pdf';
      return `${base}/${url.replace(/^\/+/, '')}`;
    },
    openPurchaseForm() {
      this.showPurchaseForm = true;
    },
    closePurchaseForm() {
      this.showPurchaseForm = false;
    }
  }
};
</script>

<style scoped>
.product-detail {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.back-button {
  background: #f5f5f5;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  margin-bottom: 20px;
  transition: all 0.3s;
}

.back-button:hover {
  background: #e0e0e0;
}

.detail-container {
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  padding: 30px;
}

.detail-header {
  border-bottom: 2px solid #f0f0f0;
  padding-bottom: 20px;
  margin-bottom: 30px;
}

.product-title {
  font-size: 28px;
  color: #2c3e50;
  margin-bottom: 15px;
}

.product-badges {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.badge {
  padding: 5px 15px;
  border-radius: 15px;
  font-size: 12px;
  font-weight: 500;
}

.badge-category {
  background: #e3f2fd;
  color: #1976d2;
}

.badge-package {
  background: #f3e5f5;
  color: #7b1fa2;
}

.badge-status {
  background: #e8f5e9;
  color: #388e3c;
}

.badge.status-developing {
  background: #fff3e0;
  color: #f57c00;
}

.badge.status-discontinued {
  background: #ffebee;
  color: #d32f2f;
}

.badge.status-preview {
  background: #e1f5fe;
  color: #0288d1;
}

.detail-section {
  margin-bottom: 30px;
}

.detail-section h2 {
  font-size: 20px;
  color: #2c3e50;
  margin-bottom: 15px;
  padding-left: 10px;
  border-left: 4px solid #42b983;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 15px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  padding: 10px;
  background: #f9f9f9;
  border-radius: 5px;
}

.info-label {
  font-weight: 500;
  color: #666;
}

.info-value {
  font-weight: bold;
  color: #2c3e50;
}

.download-link {
  display: inline-block;
  padding: 12px 24px;
  background: #42b983;
  color: white;
  text-decoration: none;
  border-radius: 5px;
  transition: all 0.3s;
}

.download-link:hover {
  background: #3aa876;
  transform: translateY(-2px);
}

.actions {
  display: flex;
  gap: 15px;
  margin-top: 30px;
}

.action-button {
  padding: 12px 30px;
  border: none;
  border-radius: 5px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
}

.action-button.primary {
  background: #42b983;
  color: white;
}

.action-button.primary:hover {
  background: #3aa876;
  transform: translateY(-2px);
}

.action-button.secondary {
  background: #f5f5f5;
  color: #2c3e50;
}

.action-button.secondary:hover {
  background: #e0e0e0;
}

.loading {
  text-align: center;
  padding: 50px;
  font-size: 18px;
  color: #999;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .product-title {
    font-size: 22px;
  }
  
  .info-grid {
    grid-template-columns: 1fr;
  }
  
  .actions {
    flex-direction: column;
  }
}
</style>
